import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { companyDetailsStorageUtils } from "@/utils/localStorageUtils";

interface CompanyDetails {
  id: number;
  company_name: string;
  status: string;
  default_new_creation_field_name: string;
  alias_new_creation_english: string;
  alias_new_creation_french: string;
  allowed_to_creation: boolean;
  primary_field: string;
  is_vin_required: boolean;
  is_debtor_required: boolean;
  is_reg_document_required: boolean;
  created_ts: string;
  updated_ts: string;
  created_by: number | null;
  updated_by: number | null;
  view_New_Opportunity_Upload: boolean;
  view_col_regDocument_upload: boolean;
  view_col_VIN_Number: boolean;
  view_dashboard_registration_document: boolean;
  view_dashboard_VIN_ID: boolean;
  view_dashboard_ppsaonvin_document: boolean;
  view_ppsaonowner: boolean;
  view_ppsaonvin: boolean;
  view_registration: boolean;
}

interface CompanyState {
  details: CompanyDetails | null;
  loading: boolean;
  error: string | null;
}

// Try to load initial state from localStorage as fallback
const loadInitialState = (): CompanyState => {
  const storageDetails = companyDetailsStorageUtils.loadCompanyDetails();

  return {
    details: storageDetails || null,
    loading: false,
    error: null,
  };
};

const initialState: CompanyState = loadInitialState();

const companySlice = createSlice({
  name: "company",
  initialState,
  reducers: {
    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },

    // Set company details data
    setCompanyDetails: (state, action: PayloadAction<CompanyDetails>) => {
      state.details = action.payload;
      state.loading = false;
      state.error = null;

      // Save to localStorage as backup
      try {
        companyDetailsStorageUtils.saveCompanyDetails(action.payload);
      } catch (error) {
        console.warn("Failed to save company details to localStorage:", error);
      }
    },

    // Set error state
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Load company details from localStorage
    loadFromStorage: (state) => {
      try {
        const storageDetails = companyDetailsStorageUtils.loadCompanyDetails();
        if (storageDetails) {
          state.details = storageDetails;
          state.error = null;
          console.info("Loaded company details from localStorage");
        }
      } catch (error) {
        console.warn(
          "Failed to load company details from localStorage:",
          error,
        );
      }
    },

    // Reset state
    reset: (state) => {
      state.details = null;
      state.loading = false;
      state.error = null;

      // Clear localStorage as well
      try {
        companyDetailsStorageUtils.clearCompanyDetails();
      } catch (error) {
        console.warn("Failed to clear company details localStorage:", error);
      }
    },

    // Update company view settings based on reg document requirement
    updateCompanyViewSettings: (
      state,
      action: PayloadAction<{
        is_reg_document_required: boolean;
        is_vin_required: boolean;
        is_debtor_required: boolean;
      }>,
    ) => {
      if (state.details) {
        const {
          is_reg_document_required,
          is_vin_required = true,
          is_debtor_required = true,
        } = action.payload;

        // Update the main field
        state.details.is_reg_document_required = is_reg_document_required;
        state.details.view_New_Opportunity_Upload = is_reg_document_required;
        state.details.view_col_regDocument_upload = is_reg_document_required;
        state.details.view_col_VIN_Number = is_reg_document_required;
        state.details.view_dashboard_registration_document =
          is_reg_document_required;
        state.details.view_dashboard_VIN_ID = is_reg_document_required;
        state.details.view_dashboard_ppsaonvin_document =
          is_reg_document_required;
        state.details.view_ppsaonowner = is_debtor_required;
        state.details.view_ppsaonvin = is_vin_required;
        state.details.view_registration = is_vin_required;

        // Save to localStorage as backup
        try {
          companyDetailsStorageUtils.saveCompanyDetails(state.details);
        } catch (error) {
          console.warn(
            "Failed to save updated company details to localStorage:",
            error,
          );
        }
      }
    },
  },
});

export const {
  setLoading,
  setCompanyDetails,
  setError,
  clearError,
  loadFromStorage,
  reset,
  updateCompanyViewSettings,
} = companySlice.actions;

export default companySlice.reducer;
